// ✅ src/pages/VehicleV2/DayWiseDailyProgressReport_Print.jsx
// Works with navigate(..., { state: { mode, filters } })

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/dayWiseDailyProgressReport";

const toStr = (v) => String(v ?? "").trim();
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

export default function DayWiseDailyProgressReport_Print() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // ✅ Read from navigation state
  const mode = toStr(state?.mode || "HOUR").toUpperCase();
  const filters = state?.filters || {};

  const [rows, setRows] = useState([]);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePrint = () => window.print();

  const fetchPrint = async () => {
    try {
      if (!filters?.month || !filters?.year) return;

      setLoading(true);

      const params = {
        mode,
        branchType: filters.branchType,
        branch: filters.branch,
        month: filters.month,
        year: filters.year,
        vehicleType: filters.vehicleType,
        vehicleCode: filters.vehicleCode,
        vehicleName: filters.vehicleName,
        regNo: filters.regNo,
      };

      const { data } = await axios.get(`${API_BASE}/print`, {
        params,
      });

      if (!data?.success) throw new Error(data?.message);

      setPayload(data);
      setRows(data.rows || []);
    } catch (err) {
      alert(err?.message || "Failed to load print data");
      setRows([]);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line
  }, []);

  const get = (obj, ...keys) => {
    for (const k of keys) {
      if (obj?.[k] !== undefined && obj?.[k] !== null && String(obj[k]).trim() !== "")
        return obj[k];
    }
    return "";
  };

  const totalDays = toInt(payload?.totalDays, 31);

  const dayCols = useMemo(() => {
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  }, [totalDays]);

  const monthName = payload?.monthName || "";
  const year = filters?.year || "";

  const now = new Date();
  const nowStr = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

  return (
    <div className="min-h-screen bg-white p-3">
      {/* Buttons hidden while printing */}
      <div className="print:hidden mb-4 flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>

        <button
          onClick={handlePrint}
          disabled={loading}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800"
        >
          {loading ? "Loading..." : "Print"}
        </button>
      </div>

      <div className="mx-auto border-2 border-black p-2 print:mx-0 print:max-w-none">
        <div className="border border-black p-2">

          {/* Header */}
          <div className="border border-black p-2 mb-2 flex justify-between items-center">
            <div className="text-xs font-bold">
              NISSI INFOTECH PVT LTD
              <div className="text-[10px] font-normal">
                CIVIL CONSTRUCTION
              </div>
            </div>
            <div className="text-[10px]">{nowStr}</div>
          </div>

          <div className="text-center font-bold text-sm my-2">
            DAILY PROGRESS REPORT FOR {monthName} {year}
          </div>

          {!rows.length ? (
            <div className="text-center p-6 border border-black text-xs">
              No Records Found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr>
                    <th className="th">S.No</th>
                    <th className="th">Branch</th>
                    <th className="th">Vehicle Main Type</th>
                    <th className="th">Vehicle Code</th>
                    <th className="th">Vehicle Reg No</th>

                    {dayCols.map((d) => (
                      <th key={d} className="th day">{d}</th>
                    ))}

                    <th className="th">Total</th>
                    <th className="th">
                      {mode === "KM" ? "CUM KM" : "CUM HR"}
                    </th>
                    <th className="th">Last up Date</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx}>
                      <td className="td center">{idx + 1}</td>
                      <td className="td">{get(r, "Branch")}</td>
                      <td className="td">{get(r, "VehicleMainType")}</td>
                      <td className="td">{get(r, "VehicleCode")}</td>
                      <td className="td">{get(r, "VehicleRegNo")}</td>

                      {dayCols.map((d) => (
                        <td key={d} className="td center day">
                          {get(r, String(d))}
                        </td>
                      ))}

                      <td className="td center">
                        {get(r, "TotalHr", "TotalKM")}
                      </td>
                      <td className="td center">
                        {get(r, "CummulativeHr", "CummulativeKM")}
                      </td>
                      <td className="td center">
                        {get(r, "LastUpdateDate")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer Sign */}
          <div className="mt-10 flex justify-between text-xs px-4">
            <div>Prepared By</div>
            <div>Checked By</div>
            <div>Approved By</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          .print\\:hidden { display: none !important; }
        }
        .th {
          border: 1px solid #000;
          padding: 4px;
          font-size: 10px;
          text-align: center;
          font-weight: bold;
          white-space: nowrap;
        }
        .td {
          border: 1px solid #000;
          padding: 4px;
          font-size: 10px;
          white-space: nowrap;
        }
        .center { text-align: center; }
        .day { width: 32px; }
      `}</style>
    </div>
  );
}
